/*
 * Relatórios: uso da rede (painel gerencial), desempenho das campanhas e
 * exportação do histórico de acessos.
 *
 * Cada bloco aparece só para quem tem a permissão dele. O painel de uso é
 * só números; o histórico de acessos traz nome e telefone, e por isso exige
 * a permissão de exportar clientes, não a de ver relatórios.
 *
 * As taxas aparecem ao lado dos números absolutos de propósito. "12 cliques"
 * não diz nada sozinho; "12 cliques em 340 impressões (3,5%)" diz.
 */
import { useEffect, useState } from 'react';
import { PERMISSIONS } from '@hotspot/shared';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { CampaignMetricDto, MikrotikDto } from '../lib/types';
import { Etiqueta } from './CustomersPage';
import { Alert, Card, Spinner } from '../components/ui';
import { UsagePanel } from './UsagePanel';

const JANELAS = [7, 30, 90] as const;

export function ReportsPage() {
  const { can } = useAuth();
  const podeVer = can(PERMISSIONS.REPORT_READ);
  const verCampanhas = podeVer && can(PERMISSIONS.CAMPAIGN_READ);
  const podeExportar = can(PERMISSIONS.CUSTOMER_EXPORT);

  const [dias, setDias] = useState<number>(30);
  const [campanhas, setCampanhas] = useState<CampaignMetricDto[] | null>(null);
  const [equipamentos, setEquipamentos] = useState<MikrotikDto[]>([]);
  const [mikrotikId, setMikrotikId] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    apiList<MikrotikDto>('/admin/mikrotiks?perPage=100')
      .then(({ data }) => setEquipamentos(data))
      .catch(() => setEquipamentos([]));
  }, []);

  useEffect(() => {
    if (!verCampanhas) return;

    let ativo = true;
    setCampanhas(null);

    apiRequest<CampaignMetricDto[]>(`/admin/reports/campaigns?dias=${dias}`)
      .then((dados) => ativo && setCampanhas(dados))
      .catch(
        (caught: unknown) =>
          ativo &&
          setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar'),
      );

    return () => {
      ativo = false;
    };
  }, [dias, verCampanhas]);

  if (!podeVer) {
    return <Alert tone="info">Seu perfil não tem acesso aos relatórios.</Alert>;
  }

  const consultaCsv = `?dias=${dias}${mikrotikId !== null ? `&mikrotikId=${mikrotikId}` : ''}`;

  return (
    <div className="space-y-12">
      <UsagePanel />

      {verCampanhas || podeExportar ? (
        <div className="space-y-6 print:hidden">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {verCampanhas ? 'Campanhas' : 'Exportação'}
              </h2>
              {verCampanhas ? (
                <p className="text-sm text-slate-500">Últimos {dias} dias, no fuso de Brasília.</p>
              ) : null}
            </div>

            {verCampanhas ? (
              <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm shadow-sm">
                {JANELAS.map((janela) => (
                  <button
                    key={janela}
                    type="button"
                    onClick={() => setDias(janela)}
                    className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                      dias === janela
                        ? 'bg-brand-600 text-white'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {janela} dias
                  </button>
                ))}
              </div>
            ) : null}
          </header>

          {erro ? <Alert>{erro}</Alert> : null}

          {verCampanhas ? (
            <Card title="Desempenho das campanhas">
              {!campanhas ? (
                <Spinner />
              ) : campanhas.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhuma campanha cadastrada.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
                        <th className="py-2 pr-3">Campanha</th>
                        <th className="py-2 pr-3 text-right">Exibições</th>
                        <th className="py-2 pr-3 text-right">Vistas até o fim</th>
                        <th className="py-2 pr-3 text-right">Cliques</th>
                        <th className="py-2 text-right">Pulos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campanhas.map((campanha) => (
                        <tr
                          key={campanha.campaignId}
                          className="border-b border-slate-100 last:border-0"
                        >
                          <td className="py-2.5 pr-3">
                            <span className="font-medium text-slate-800">{campanha.title}</span>
                            {!campanha.isActive ? (
                              <span className="ml-2">
                                <Etiqueta tom="slate">inativa</Etiqueta>
                              </span>
                            ) : null}
                          </td>
                          <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">
                            {campanha.impressoes}
                          </td>
                          <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">
                            {campanha.concluidas}
                            <Taxa valor={campanha.taxaDeConclusao} />
                          </td>
                          <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">
                            {campanha.cliques}
                            <Taxa valor={campanha.taxaDeClique} />
                          </td>
                          <td className="py-2.5 text-right tabular-nums text-slate-500">
                            {campanha.pulos}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <p className="mt-3 text-xs text-slate-500">
                    <strong>Exibições</strong> conta uma vez por sessão, não por imagem repetida.{' '}
                    <strong>Vistas até o fim</strong> são as que cumpriram o tempo mínimo
                    configurado na propaganda. <strong>Pulos</strong> são as que o cliente avançou
                    antes disso — não é erro, é a opção que você deixou disponível.
                  </p>
                </div>
              )}
            </Card>
          ) : null}

          {podeExportar ? (
            <Card title="Histórico de acessos">
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Exporta uma linha por sessão do período: data, cliente, telefone, MAC, IP,
                  equipamento, modo de acesso e duração.
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  {equipamentos.length > 0 ? (
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      Equipamento
                      <select
                        value={mikrotikId ?? ''}
                        onChange={(evento) =>
                          setMikrotikId(evento.target.value ? Number(evento.target.value) : null)
                        }
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                      >
                        <option value="">Todos</option>
                        {equipamentos.map((equipamento) => (
                          <option key={equipamento.id} value={equipamento.id}>
                            {equipamento.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}

                  <a
                    href={`/api/admin/reports/accesses.csv${consultaCsv}`}
                    className="inline-flex items-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
                  >
                    Exportar CSV
                  </a>
                </div>

                <p className="text-xs text-slate-500">
                  O arquivo sai em UTF-8 com BOM e separador ponto e vírgula — abre direto no Excel
                  em português, com acentuação correta.
                </p>
              </div>
            </Card>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Percentual ao lado do absoluto: o número sozinho não permite comparar. */
function Taxa({ valor }: { valor: number }) {
  if (valor === 0) return null;
  return <span className="ml-1.5 text-xs font-normal text-slate-500">({valor}%)</span>;
}
